import { endWaitlistConversion, refreshWaitlistsThunk } from 'actions/waitlists/WaitlistsActions';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { FunctionComponent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WaitlistsConversionState, WaitlistsPanelState } from 'reducers/waitlists/WaitlistsState';
import { RootState } from 'store';

// A recurring save marks the waitlist as converted in background, seconds after the response.
const RECURRING_CONVERSION_REFRESH_MS = 5000;

// Rendered once in the app shell, next to the waitlist panel.
export const WaitlistConversionDrawer: FunctionComponent = () => {
    const dispatch = useDispatch();

    const conversion = useSelector<RootState, WaitlistsConversionState | undefined>(
        state => state.waitlists.conversion
    );
    const panel = useSelector<RootState, WaitlistsPanelState>(state => state.waitlists.panel);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    // The booking can be saved and the drawer closed with the X instead of finishing the flow.
    const bookedRef = useRef(false);
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(refreshTimeoutRef.current), []);

    useEffect(() => {
        bookedRef.current = false;
    }, [conversion?.waitlist.id]);

    useEffect(() => {
        if (
            conversion &&
            [NewBookingsStatus.SaveMultipleSuccess, NewBookingsStatus.SaveMultipleBookingForDepositSuccess].includes(
                newBookingsStatus
            )
        ) {
            bookedRef.current = true;
        }
    }, [newBookingsStatus]);

    if (!conversion) {
        return null;
    }

    const bookedHandler = () => {
        const { marketplaceIds } = panel;

        dispatch(endWaitlistConversion(true));
        dispatch(refreshWaitlistsThunk(marketplaceIds));

        // Don't assume it was converted: refresh again once a recurring save had time to mark it.
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(
            () => dispatch(refreshWaitlistsThunk(marketplaceIds)),
            RECURRING_CONVERSION_REFRESH_MS
        );
    };

    return (
        <CreateBookingDrawer
            parentID={conversion.waitlist.customerId}
            at={conversion.appointments[0]?.variableData[0]?.date}
            multipleAppointments={conversion.appointments}
            waitlistId={conversion.waitlist.id}
            marketplaceId={conversion.waitlist.marketplaceId}
            onBooked={bookedHandler}
            onClose={() => (bookedRef.current ? bookedHandler() : dispatch(endWaitlistConversion(false)))}
        />
    );
};

export default WaitlistConversionDrawer;
